import React from "react";
import PropTypes from "prop-types";
import {
  Modal, Slide, Box, Stack, Typography, IconButton, Button, CircularProgress,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import {
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  PRIMARY_COLOR,
  ERROR_COLOR,
  secondaryButtonStyle,
} from "./wafEventDrawerShared";
import { spDrawerInsetSx, SP, spDrawerShellSx, spHeroHeaderShellSx, spScrollBodySx, spDrawerFooterSx, spLh } from "./v3/smartParseDrawerUi";

export default function WafEventDrawerShell({
  open,
  onClose,
  title,
  icon = DETECTION_ICONS.wafDetection,
  detailLine,
  eventId,
  onCopyEventId,
  loading = false,
  error = null,
  children,
  iconBg = "#EEF0FE",
  iconBorder = "#E5E7FF",
  headerContent = null,
  heroContent = null,
  headerVariant = "default",
  scrollHeaderWithBody = false,
  footerLeft = null,
  footerActions = null,
  customFooter = null,
}) {
  const showFixedHeader = headerContent && !scrollHeaderWithBody;
  const heroHeader = headerVariant === "hero";

  return (
    <Modal open={open} onClose={onClose} BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.2)", backdropFilter: "blur(2px)" } }}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: SP.drawerWidth,
            maxWidth: "100vw",
            height: "100vh",
            zIndex: 1300,
            outline: "none",
            display: "flex",
            flexDirection: "column",
            ...spDrawerShellSx,
          }}
        >
          {scrollHeaderWithBody && (
            <IconButton
              onClick={onClose}
              aria-label="Close drawer"
              sx={{
                position: "absolute",
                top: 20,
                right: 24,
                width: 36,
                height: 36,
                borderRadius: "8px",
                zIndex: 3,
                backgroundColor: "#fff",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              <LayerdIcon icon={DETECTION_ICONS.close} size={20} color="neutral" />
            </IconButton>
          )}

          {showFixedHeader && (
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={heroHeader ? spHeroHeaderShellSx : {
                px: 3,
                py: 2.5,
                borderBottom: `1px solid ${BORDER_COLOR}`,
                background: "linear-gradient(180deg, #FFFFFF 0%, #F8F9FF 100%)",
                flexShrink: 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, pr: 1, position: "relative", zIndex: 1 }}>{headerContent}</Box>
              <IconButton
                onClick={onClose}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: heroHeader ? "10px" : "8px",
                  flexShrink: 0,
                  color: TEXT_SECONDARY,
                  border: heroHeader ? "1px solid transparent" : "none",
                  position: "relative",
                  zIndex: 1,
                  "&:hover": { backgroundColor: "rgba(40, 46, 100, 0.055)", borderColor: BORDER_COLOR, color: TEXT_PRIMARY },
                }}
              >
                <LayerdIcon icon={DETECTION_ICONS.close} size={20} color="neutral" />
              </IconButton>
            </Stack>
          )}

          {!showFixedHeader && !scrollHeaderWithBody && (
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${BORDER_COLOR}`, flexShrink: 0 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "8px",
                    backgroundColor: iconBg,
                    border: `1px solid ${iconBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LayerdIcon icon={icon} size={20} color="primary" />
                </Box>
                <Stack spacing={0.25}>
                  <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: spLh.px24, color: TEXT_PRIMARY }}>
                    {title}
                  </Typography>
                  {detailLine && (
                    <Typography sx={{ fontSize: 14, lineHeight: spLh.px21, color: TEXT_SECONDARY }}>
                      {detailLine}
                    </Typography>
                  )}
                  {eventId && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontSize: 12, color: PRIMARY_COLOR }}>
                        {eventId}
                      </Typography>
                      {onCopyEventId && (
                        <IconButton
                          size="small"
                          onClick={() => onCopyEventId(eventId)}
                          aria-label="Copy event ID"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "6px",
                            color: PRIMARY_COLOR,
                            "&:hover": { backgroundColor: "#EEF0FE" },
                          }}
                        >
                          <LayerdIcon icon={DETECTION_ICONS.copy} size={14} color="primary" />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Stack>
              <IconButton
                onClick={onClose}
                sx={{ width: 36, height: 36, borderRadius: "8px", flexShrink: 0, "&:hover": { backgroundColor: "#F3F4F6" } }}
              >
                <LayerdIcon icon={DETECTION_ICONS.close} size={20} color="neutral" />
              </IconButton>
            </Stack>
          )}

          <Box sx={{ flex: 1, overflow: "hidden", position: "relative", minHeight: 0 }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.72)",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={28} sx={{ color: PRIMARY_COLOR }} />
              </Box>
            )}

            <ReactPerfectScrollbar>
              <Stack
                sx={heroHeader ? spScrollBodySx : scrollHeaderWithBody ? { ...spDrawerInsetSx, gap: "14px" } : { p: `${SP.drawer}px`, gap: "14px" }}
              >
                {heroHeader && heroContent}
                {error && (
                  <Typography sx={{ fontSize: 14, color: ERROR_COLOR }}>{error}</Typography>
                )}
                {children}
              </Stack>
            </ReactPerfectScrollbar>
          </Box>

          {customFooter || (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={spDrawerFooterSx}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>{footerLeft}</Box>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                {footerActions || (
                  <Button onClick={onClose} sx={secondaryButtonStyle}>
                    Close
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Slide>
    </Modal>
  );
}

WafEventDrawerShell.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  detailLine: PropTypes.string,
  eventId: PropTypes.string,
  onCopyEventId: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node,
  iconBg: PropTypes.string,
  iconBorder: PropTypes.string,
  headerContent: PropTypes.node,
  heroContent: PropTypes.node,
  headerVariant: PropTypes.oneOf(["default", "hero"]),
  scrollHeaderWithBody: PropTypes.bool,
  footerLeft: PropTypes.node,
  footerActions: PropTypes.node,
  customFooter: PropTypes.node,
};
